import { action } from "storybook/actions";
import React, { useMemo } from "react";
import { ScrollView } from "react-native";
import VStack from "../../ui_atoms/VStack";
import RootScreenHeader from "../../screens/user/ScreenHeader";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import type { Meta, StoryObj } from "@storybook/react";
import { buildUser } from "../factories";
import { ProfileType } from "../../__generated__/graphql";

const StoryComponent = ({ profileType }: { profileType: ProfileType }) => {
    const user = useMemo(() => buildUser(), []);
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <ScrollView style={{ flex: 1, width: "100%" }}>
                    <VStack gap={0.5} alignment="stretch" style={{ width: "100%" }}>
                        <RootScreenHeader label="Simple label" />
                        <RootScreenHeader
                            label="With actions"
                            actions={[
                                {
                                    icon: "images",
                                    label: "First",
                                    onPress: action("First action"),
                                },
                                {
                                    icon: "age",
                                    label: "Second",
                                    onPress: action("Second action"),
                                },
                                {
                                    icon: "cct",
                                    label: "Third",
                                    onPress: action("Third action"),
                                    color: "red",
                                    style: { borderTopWidth: 1, borderColor: "yellow" },
                                },
                            ]}
                        />
                        <RootScreenHeader label="Simple label" subLabel="With sub label" />
                        <RootScreenHeader label="Long label spawn on multiple lines" />
                        {/* Large title made of one unbreakable word: shrinks instead of wrapping. */}
                        <RootScreenHeader label="Stundenerfassung" />
                        <RootScreenHeader label="Simple label" onLeftAction={action("Go back")} />
                        {/* Nav-bar titles, worst cases taken from the real translations: fits at
                          * full size / shrinks to fit / hits the font-scale floor and ellipsizes. */}
                        {["Arbeitsvertrag", "Stundenerfassung", "Frais à la charge de l'employeur"].map(
                            (label) => (
                                <RootScreenHeader
                                    key={label}
                                    useSmallLabel
                                    label={label}
                                    onLeftAction={action("Go back")}
                                    actions={[
                                        {
                                            icon: "images",
                                            label: "First",
                                            onPress: action("First action"),
                                        },
                                    ]}
                                />
                            ),
                        )}
                        <RootScreenHeader
                            label="Simple label"
                            subLabel="With sub label"
                            onLeftAction={action("Go back")}
                        />
                        <RootScreenHeader
                            label="Simple label"
                            subLabel="With sub label"
                            leftIcon="close"
                            leftActionDisabled
                            onLeftAction={action("Close")}
                        />
                        <RootScreenHeader
                            label="Simple label"
                            subLabel="With sub label and actions"
                            leftIcon="close"
                            leftActionDisabled
                            onLeftAction={action("Close")}
                            actions={[
                                {
                                    icon: "images",
                                    label: "First",
                                    onPress: action("First action"),
                                },
                                {
                                    icon: "age",
                                    label: "Second",
                                    onPress: action("Second action"),
                                },
                                {
                                    icon: "cct",
                                    label: "Third",
                                    onPress: action("Third action"),
                                    color: "red",
                                    style: { borderTopWidth: 1, borderColor: "yellow" },
                                },
                            ]}
                        />
                    </VStack>
                </ScrollView>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Components/Screen header",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
    },
    args: {},
    decorators: [
        (StoryFn) => (
            <ScrollView>
                <StoryFn />
            </ScrollView>
        ),
    ],
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};
